import { useCallback, useEffect } from 'react';

import { TrackPageArgs, useAnalytics } from '../AnalyticsProvider';
import { EventProperties, NoData } from '../events';

export type EventName = keyof EventProperties;

/**
 * PRIVATE: internal function for calling any analytics function with any data
 * It's used so that useAnalyticsEvent/useFireAnalyticsEvent can simplify their external type safety without changing functionality
 *
 * It is NOT intented for external consumption - please do NOT consume/export without consultation
 */
const _useUnsafeAnalyticsEvent = (eventName: EventName) => {
	const { trackEvent } = useAnalytics();

	const trackFn = useCallback(
		async (extraData = {}) => {
			console.debug(
				`[AnalyticsProvider.useAnalyticsEvent.trigger] Send event '${eventName}': `,
				extraData,
			);
			trackEvent(eventName, extraData);
		},
		[trackEvent, eventName],
	);

	return trackFn;
};

/**
 * Use this hook when you need to get a function to call the analytics with.
 * Returns a function which will fire the event named when called with optional extra data.
 *
 * @param eventName EventName
 * @returns A function to fire the event named, with the optional extra data
 */
export const useAnalyticsEvent = <TEventName extends EventName>(
	eventName: TEventName,
): ((
	...[extraData]: EventProperties[TEventName] extends NoData
		? []
		: [EventProperties[TEventName]]
) => Promise<void>) => {
	return _useUnsafeAnalyticsEvent(eventName);
};

/**
 * Fires the event named with the optional extra data whenever the dependencies specified change.
 *
 * @param props EventName, optional extra data, and change dependency array
 */

type UseFireAnalyticsEventProps<TEventName extends EventName> = {
	eventName: TEventName;
	extraData?: EventProperties[TEventName] extends NoData
		? never
		: EventProperties[TEventName];
	deps?: unknown[];
	enabled?: boolean;
};
export const useFireAnalyticsEvent = <TEventName extends EventName>({
	eventName,
	extraData,
	deps = [],
	enabled = true,
}: UseFireAnalyticsEventProps<TEventName>) => {
	const triggerEvent = _useUnsafeAnalyticsEvent(eventName);

	// This gets called multiple times if `useMemo` is used instead!
	useEffect(() => {
		if (!enabled) {
			return;
		}
		triggerEvent(extraData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [...deps, enabled]);
};

type FireAnalyticsPageViewArgs = {
	deps?: unknown[];
	enabled?: boolean;
	pageViewArgs?: TrackPageArgs;
};

export const useFireAnalyticsPageView = (args?: FireAnalyticsPageViewArgs) => {
	const { deps = [], enabled = true, pageViewArgs } = args || {};
	const { trackPage } = useAnalytics();

	// This gets called multiple times if `useMemo` is used instead!
	useEffect(() => {
		if (!enabled) {
			return;
		}
		trackPage(pageViewArgs);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [...deps, enabled]);
};
